import React, { useEffect, useState } from "react";
import { Scale, ExternalLink, TrendingUp, TrendingDown, Minus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * ComparePanel — shows deep-link search URLs to competitor platforms
 * (Naukri/LinkedIn/Shaadi/MakeMyTrip/Zomato etc.) plus a market-range
 * estimate from Find2Near's own data.
 *
 * We do NOT scrape competitor prices — ToS forbid it. We just help the user
 * open the same query on those sites in one tap.
 */
export default function ComparePanel({ category, query, city, price, checkin, checkout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.post("/compare", { category, query, city, price, checkin, checkout })
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [category, query, city, price, checkin, checkout]);

  if (loading) {
    return (
      <div className="mt-4 rounded-2xl border border-stone-200 p-4 flex items-center gap-2 text-sm text-stone-500" data-testid="compare-loading">
        <Loader2 size={14} className="animate-spin" /> Comparing across platforms…
      </div>
    );
  }
  if (!data || !data.compare_on || data.compare_on.length === 0) return null;

  const mr = data.market_range;
  const VerdictIcon = mr?.verdict === "below_market" ? TrendingDown
                    : mr?.verdict === "above_market" ? TrendingUp
                    : Minus;
  const verdictClass = mr?.verdict === "below_market"
    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
    : mr?.verdict === "above_market"
      ? "bg-rose-50 border-rose-200 text-rose-800"
      : "bg-stone-50 border-stone-200 text-stone-700";

  return (
    <div className="mt-6 rounded-2xl border border-stone-200 bg-white overflow-hidden" data-testid="compare-panel">
      <div className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-[#DDA77B]/15 via-white to-[#2D5A27]/10 border-b border-stone-200">
        <Scale size={16} className="text-[#2D5A27]" />
        <div className="font-bold text-stone-900">Compare on other platforms</div>
        <span className="ml-auto text-[10px] uppercase tracking-widest font-bold text-stone-500">deep links</span>
      </div>

      {mr && (
        <div className={`mx-5 mt-4 rounded-xl border ${verdictClass} px-4 py-3 flex items-start gap-3`} data-testid="compare-verdict">
          <VerdictIcon size={18} className="mt-0.5 shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-bold">
              {mr.verdict === "below_market" && "Below market — good deal 💚"}
              {mr.verdict === "above_market" && "Above typical range — worth a comparison"}
              {mr.verdict === "fair_market" && "Fair-market pricing"}
              {!mr.verdict && "Market range"}
            </div>
            <div className="text-xs mt-0.5">
              Typical <b>{city || "regional"}</b> range (from {mr.samples} similar listings):
              &nbsp;₹{Number(mr.p25).toLocaleString("en-IN")} – ₹{Number(mr.p75).toLocaleString("en-IN")}
              &nbsp;· median <b>₹{Number(mr.median).toLocaleString("en-IN")}</b>
              {mr.unit && mr.unit !== "fixed" ? ` / ${mr.unit}` : ""}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 p-4">
        {data.compare_on.map(c => (
          <a
            key={c.name}
            href={c.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 border border-stone-200 rounded-xl px-3 py-2 hover:border-[#E05E36] hover:shadow-sm transition-all group"
            data-testid={`compare-${c.name.toLowerCase().replace(/\s+/g,'-')}`}
            style={{ borderTop: `3px solid ${c.logo_bg}` }}
          >
            <div className="w-7 h-7 rounded-md text-white text-[10px] font-bold grid place-items-center shrink-0" style={{ backgroundColor: c.logo_bg }}>
              {c.name.slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold text-stone-900 truncate">{c.name}</div>
              <div className="text-[10px] text-stone-500 flex items-center gap-0.5">Search <ExternalLink size={9} /></div>
            </div>
          </a>
        ))}
      </div>

      <div className="text-[11px] text-stone-500 px-5 pb-4">
        We link to search results on other platforms — we don&apos;t fetch their prices (their ToS prohibits it). Rates &amp; availability there update independently.
      </div>
    </div>
  );
}
